import {isUndefined} from 'lodash';
import {transparentize} from 'polished';
import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {globalWindow} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {useWasUnmounted} from '../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {AsyncHandlerOf, Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {
  InvalidPhoneNumberError,
  isValidPhoneNumber,
} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {InlineLinkButton} from '../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {Icon, IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ContactInformationContact} from '../../../../srcCommon/inboxes/contactDetails/contactDetailsHelpers';
import {InvalidEmailAddressError, isValidEmailAddress} from '../../../helpers/emailHelpers';
import {useClipboard} from '../../../helpers/hooks/clipboardHookHelpers';
import {
  addContact,
  CardAttributeUpdateAction,
  deleteContact,
  updateContact,
} from '../../../helpers/models/cardAttributeUpdateHelpers';
import {
  computeMergeCardDetails,
  shouldShowDuplicateCardInidicator,
} from '../../../helpers/models/cardHelpers';
import {computeIsGlobalNamespace, isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {buildContactUid, ContactSourcesEnum} from '../../../models/api/contactModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../services/tracking';
import {InlineEditingContactTextBox} from '../../common/inlineEditing/inlineEditingContactTextBox';
import {useInlineEditing} from '../../common/inlineEditing/useInlineEditing';
import {ComplexTooltipContent} from '../../common/tooltips/complexTooltipContent';
import {useContactDetailsContext} from '../contactDetailsContext';
import {
  buildClickTrackingPayload,
  buildContactDetailsHoverPayload,
  ContactDetailsClickActionsEnum,
  ContactDetailsClickLocationsEnum,
  contactDetailsHoverOverTimeToReport,
  ContactDetailsHoverSectionsEnum,
} from '../contactDetailsTrackingHelpers';

/*
 * Constants.
 */

const maxTooltipContainerWidth = 224;

/*
 * Types.
 */

interface ContactProps {
  card: CardModel | undefined;
  contact: ContactInformationContact;
  isMainHandle: boolean;
  duplicateCard?: CardModel;
  hasChannel: boolean;
  onComposeMessage: HandlerOf<ContactInformationContact>;
  updateCardAttributeAsync: AsyncHandlerOf<CardAttributeUpdateAction>;
  updateUnsavedCard: HandlerOf<CardAttributeUpdateAction>;
  openContactDetailsPopUp: HandlerOf<number>;
}

/*
 * Style.
 */

const StyledContactDiv = styled.div`
  display: flex;
  align-items: center;
  margin: 7px 0;
  position: relative;
`;

const StyledIconDiv = styled.div`
  padding-right: 8px;
`;

const StyledTextDiv = styled.div`
  flex: 1;
  ${ellipsis()};
`;

const StyledInlineLinkButton = styled(InlineLinkButton)`
  flex: 1;
  ${ellipsis()};
`;

const StyledControlsDiv = styled.div`
  position: absolute;
  right: 0px;
  padding-left: 16px;
  background: linear-gradient(
    to right,
    ${(p) => transparentize(1, p.theme.foreground)} 0%,
    ${(p) => p.theme.foreground} 16px
  );
  display: flex;
`;

const StyledDuplicateIndicatorContainerDiv = styled.div`
  flex: 0 0 26px;
  padding: 0 5px;
`;

const StyledDuplicateIndicatorButtonContainerDiv = styled.div`
  height: 26px;
  padding: 5px 5px 0 5px;
`;

const StyledDuplicateTooltipContainerDiv = styled.div`
  display: flex;
  flex-flow: row;
  width: ${maxTooltipContainerWidth}px;
`;

const StyledDuplicateTitleDiv = styled.div`
  flex: 1;
  padding-left: 12px;
`;

const StyledTooltipActionsContainerDiv = styled.div`
  margin-top: 8px;
`;

const StyledButtonContentDiv = styled.div`
  color: ${(p) => p.theme.greys.white};
`;

/*
 * Component.
 */

export const Contact: FC<ContactProps> = (props) => {
  const theme = useUserTheme();
  const {
    card,
    contact,
    duplicateCard,
    isMainHandle,
    hasChannel,
    onComposeMessage,
    updateCardAttributeAsync,
    updateUnsavedCard,
    openContactDetailsPopUp,
  } = props;
  const [shouldShowControls, setShouldShowControls] = useState(false);
  const [wasJustCopied, setWasJustCopied] = useState(false);
  const [duplicateTimeoutId, setDuplicateTimeoutId] = useState<number | undefined>(undefined);
  const {copy} = useClipboard();
  const contactDetailsContext = useContactDetailsContext();
  const {wasUnmountedRef} = useWasUnmounted();

  const isAddingNewContact = contact?.handle === '';
  const isEditingAvailable = !isMainHandle && contact.source !== ContactSourcesEnum.FACEBOOK;

  // Check if we can show the duplicate indicator
  const shouldShowDuplicateIndicator =
    !contactDetailsContext.mergeContext.isMerging &&
    !contactDetailsContext.isReadOnly &&
    isMainHandle &&
    !isUndefined(card) &&
    !isUndefined(duplicateCard) &&
    shouldShowDuplicateCardInidicator(card, duplicateCard);

  const onDuplicateTooltipEnter = () => {
    if (!duplicateTimeoutId && card) {
      setDuplicateTimeoutId(
        globalWindow.setInstrumentedTimeout(() => {
          trackInterfaceEvent(
            InterfaceEventsEnum.P4_CONTACT_DETAILS_HOVER,
            buildContactDetailsHoverPayload(card, ContactDetailsHoverSectionsEnum.DUPLICATE_WARNING),
          );
          // If the component was unmounted, do not try to set the value
          if (!wasUnmountedRef.current) {
            setDuplicateTimeoutId(undefined);
          }
        }, contactDetailsHoverOverTimeToReport),
      );
    }
  };

  const onDuplicateTooltipExit = () => {
    if (duplicateTimeoutId) {
      globalWindow.clearTimeout(duplicateTimeoutId);
      // If the component was unmounted, do not try to set the value
      if (!wasUnmountedRef.current) {
        setDuplicateTimeoutId(undefined);
      }
    }
  };

  const onMergeDuplicateClick = () => {
    // If we do not have a duplicate card, do not merge
    if (!duplicateCard || !card) {
      return;
    }

    const {mainCardId, mergeCardIds} = computeMergeCardDetails(card, duplicateCard);
    contactDetailsContext.mergeContext.startMerge(mainCardId, mergeCardIds);
    trackInterfaceEvent(
      InterfaceEventsEnum.P4_CONTACT_DETAILS_CLICK,
      buildClickTrackingPayload(
        ContactDetailsClickActionsEnum.MERGE,
        ContactDetailsClickLocationsEnum.TOOLTIP,
        card,
      ),
    );
  };

  const onViewClick = () => {
    if (duplicateCard) {
      openContactDetailsPopUp(duplicateCard.id);
      trackInterfaceEvent(
        InterfaceEventsEnum.P4_CONTACT_DETAILS_CLICK,
        buildClickTrackingPayload(
          ContactDetailsClickActionsEnum.VIEW_DUPLICATE,
          ContactDetailsClickLocationsEnum.TOOLTIP,
          duplicateCard,
        ),
      );
    }
  };

  const onSave = (newValue: string) => {
    const action = computeOnSaveAction(isAddingNewContact, contact, newValue);
    return updateCardAttributeAsync(action);
  };

  const inlineEditingProps = useInlineEditing({
    currentAttributeValue: contact.handle,
    onSave,
    shouldStartInEditMode: isAddingNewContact,
    validator: makeContactValidator(contact),
  });

  const onCopyClick = () => {
    copy(contact.handle);
    setWasJustCopied(true);
    if (card) {
      trackInterfaceEvent(
        InterfaceEventsEnum.P4_CONTACT_DETAILS_CLICK,
        buildClickTrackingPayload(
          ContactDetailsClickActionsEnum.COPY,
          ContactDetailsClickLocationsEnum.BODY,
          card,
        ),
      );
    }
  };

  const onMouseEnterOrMoveHandler = () => {
    // Don't show the hover controls while the user is editing or saving
    if (inlineEditingProps.isEditing || inlineEditingProps.isSaving) {
      return;
    }

    setShouldShowControls(true && !contactDetailsContext.isReadOnly);
  };

  const onMouseLeaveHandler = () => {
    setShouldShowControls(false);
    setWasJustCopied(false);
  };

  const startEditing = () => {
    setShouldShowControls(false);
    inlineEditingProps.onEditStart();
  };

  const onDelete = () => {
    const action = deleteContact(contact.uid);

    // No need to hit the backend if the user deletes the contact before it's saved
    if (isAddingNewContact) {
      return updateUnsavedCard(action);
    }

    return updateCardAttributeAsync(action);
  };

  if (inlineEditingProps.isEditing) {
    return (
      <InlineEditingContactTextBox
        value={inlineEditingProps.editedValue}
        onEdit={inlineEditingProps.onEdit}
        onEditSave={inlineEditingProps.onEditSave}
        onEditCancel={inlineEditingProps.onEditCancel}
        iconName={getContactSourceIconName(contact.source)}
        error={inlineEditingProps.error}
      />
    );
  }

  return (
    <StyledContactDiv
      onMouseEnter={onMouseEnterOrMoveHandler}
      onMouseMove={onMouseEnterOrMoveHandler}
      onMouseLeave={onMouseLeaveHandler}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...buildInteractionIdProps(`contact-${contact.uid}`)}
    >
      <StyledIconDiv>
        <Icon name={getContactSourceIconName(contact.source)} color={theme.greys.shade70} />
      </StyledIconDiv>
      {renderHandle(card, contact, onComposeMessage, contactDetailsContext.isReadOnly || !hasChannel)}
      {shouldShowControls && (
        <StyledControlsDiv>
          {renderCopyButton(wasJustCopied, onCopyClick)}
          {isEditingAvailable && renderEditButton(startEditing)}
          {!isMainHandle && renderDeleteButton(onDelete)}
          {shouldShowDuplicateIndicator &&
            !isUndefined(duplicateCard) &&
            renderDuplicateIndicatorButton(
              duplicateCard,
              onMergeDuplicateClick,
              onViewClick,
              onDuplicateTooltipEnter,
              onDuplicateTooltipExit,
            )}
        </StyledControlsDiv>
      )}
      {shouldShowDuplicateIndicator && !isUndefined(duplicateCard) && (
        <StyledDuplicateIndicatorContainerDiv
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...buildInteractionIdProps('contact-duplicate-indicator')}
        >
          <Icon name="notificationStrokeSmall" color={theme.greys.shade90} />
        </StyledDuplicateIndicatorContainerDiv>
      )}
    </StyledContactDiv>
  );
};

const renderCopyButton = (wasJustCopied: boolean, onCopyClick: Handler) => (
  <>
    {wasJustCopied ? (
      <UserThemeConsumer>
        {(theme) => (
          <IconButton
            iconName="checkmarkBoxSmall"
            label={<Trans id="contact-details-contact-information-copied">Copied!</Trans>}
            hoverColor={theme.palette.green.shade40}
          />
        )}
      </UserThemeConsumer>
    ) : (
      <UserThemeConsumer>
        {(theme) => (
          <IconButton
            iconName="copySmall"
            color={theme.greys.shade70}
            label={<Trans id="contact-details-contact-information-copy">Copy</Trans>}
            onClick={onCopyClick}
          />
        )}
      </UserThemeConsumer>
    )}
  </>
);

const renderEditButton = (startEditing: Handler) => (
  <UserThemeConsumer>
    {(theme) => (
      <IconButton
        iconName="editSmall"
        color={theme.greys.shade70}
        label={<Trans id="contact-details-contact-edit">Edit</Trans>}
        onClick={startEditing}
      />
    )}
  </UserThemeConsumer>
);

const renderDeleteButton = (onDelete: Handler) => (
  <UserThemeConsumer>
    {(theme) => (
      <IconButton
        iconName="trashSmall"
        color={theme.greys.shade70}
        label={<Trans id="contact-details-contact-delete">Delete</Trans>}
        onClick={onDelete}
      />
    )}
  </UserThemeConsumer>
);

const renderDuplicateIndicatorButton = (
  duplicateCard: CardModel,
  onMergeDuplicateClick: Handler,
  onViewClick: Handler,
  onDuplicateTooltipEnter: Handler,
  onDuplicateTooltipExit: Handler,
) => (
  <UserThemeConsumer>
    {(theme) => (
      <div
        onMouseEnter={() => {
          onDuplicateTooltipEnter();
        }}
        onMouseLeave={() => {
          onDuplicateTooltipExit();
        }}
      >
        <TooltipCoordinator
          render={({onRequestTooltipClose}) =>
            renderDuplicateIndicatorTooltip(
              duplicateCard,
              onMergeDuplicateClick,
              onViewClick,
              onRequestTooltipClose,
            )
          }
          isMultiline
          placement="top"
          delay={0}
          persistent
          interactionId="contact-duplicate-indicator-btn"
          renderTarget={(isTooltipVisible) => (
            <StyledDuplicateIndicatorButtonContainerDiv>
              <Icon
                name={isTooltipVisible ? 'notificationSmall' : 'notificationStrokeSmall'}
                color={theme.greys.shade90}
                hoverColor={theme.greys.shade90}
              />
            </StyledDuplicateIndicatorButtonContainerDiv>
          )}
        />
      </div>
    )}
  </UserThemeConsumer>
);

const renderDuplicateIndicatorTooltip = (
  duplicateCard: CardModel,
  onMergeDuplicateClick: Handler,
  onViewClick: Handler,
  onRequestTooltipClose: Handler,
) => (
  <ComplexTooltipContent
    maxWidth={maxTooltipContainerWidth}
    title={renderDuplicateTooltipTitle(duplicateCard.namespace)}
    body={
      <StyledTooltipActionsContainerDiv>
        <ButtonGroup>
          <ButtonGroupSpacer />
          <Button
            variant={ButtonVariantsEnum.LINK}
            size={VisualSizesEnum.SMALL}
            onClick={() => {
              onMergeDuplicateClick();
              onRequestTooltipClose();
            }}
          >
            <StyledButtonContentDiv>
              <Trans id="contacts-duplicate-merge-button">Merge</Trans>
            </StyledButtonContentDiv>
          </Button>
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            size={VisualSizesEnum.SMALL}
            onClick={() => {
              onViewClick();
              onRequestTooltipClose();
            }}
          >
            <Trans id="contacts-duplicate-view-button">View</Trans>
          </Button>
        </ButtonGroup>
      </StyledTooltipActionsContainerDiv>
    }
  />
);

const renderDuplicateTooltipTitle = (namespace: string) => {
  if (isTeammateNamespace(namespace)) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledDuplicateTooltipContainerDiv>
            <Icon name="lockSmall" color={theme.greys.white} />
            <StyledDuplicateTitleDiv>
              <Trans id="contact-details-private-contact-found">Private copy found</Trans>
            </StyledDuplicateTitleDiv>
          </StyledDuplicateTooltipContainerDiv>
        )}
      </UserThemeConsumer>
    );
  }
  if (computeIsGlobalNamespace(namespace)) {
    return (
      <UserThemeConsumer>
        {(theme) => (
          <StyledDuplicateTooltipContainerDiv>
            <Icon name="sharedFilledSmall" color={theme.greys.white} />
            <StyledDuplicateTitleDiv>
              <Trans id="contact-details-shared-contact-found">Shared copy found</Trans>
            </StyledDuplicateTitleDiv>
          </StyledDuplicateTooltipContainerDiv>
        )}
      </UserThemeConsumer>
    );
  }
  return null;
};

/*
 * Helpers.
 */

function getContactSourceIconName(contactSource: ContactSourcesEnum): IconName {
  switch (contactSource) {
    case ContactSourcesEnum.TWITTER:
    case ContactSourcesEnum.TWITTER_DM:
      return 'twitterSmall';

    case ContactSourcesEnum.CUSTOM:
      return 'channelsSmall';

    case ContactSourcesEnum.PHONE:
      return 'phoneSmall';

    case ContactSourcesEnum.FACEBOOK:
      return 'facebookSmall';

    default:
      return 'messageSmall';
  }
}

function renderHandle(
  card: CardModel | undefined,
  contact: ContactInformationContact,
  onComposeMessage: HandlerOf<ContactInformationContact>,
  isReadOnly: boolean,
) {
  if (contact.handle === '') {
    return '-';
  }
  if (isReadOnly) {
    return <StyledTextDiv>{contact.handle}</StyledTextDiv>;
  }
  return (
    <StyledInlineLinkButton
      onClick={() => {
        onComposeMessage(contact);
        if (card) {
          trackInterfaceEvent(
            InterfaceEventsEnum.P4_CONTACT_DETAILS_CLICK,
            buildClickTrackingPayload(
              ContactDetailsClickActionsEnum.COMPOSE,
              ContactDetailsClickLocationsEnum.BODY,
              card,
            ),
          );
        }
      }}
    >
      {contact.handle}
    </StyledInlineLinkButton>
  );
}

function computeOnSaveAction(
  isAddingNewContact: boolean,
  contact: ContactInformationContact,
  newValue: string,
) {
  if (isAddingNewContact) {
    return addContact(buildContactUid(contact.source, newValue));
  }

  // If the user empties the field, treat it as a delete
  if (!newValue) {
    return deleteContact(contact.uid);
  }

  return updateContact(contact.handle, newValue, contact.uid);
}

function makeContactValidator(contact: ContactInformationContact) {
  return (value: string) => {
    if (contact.source === ContactSourcesEnum.EMAIL && !isValidEmailAddress(value)) {
      throw new InvalidEmailAddressError();
    }

    if (contact.source === ContactSourcesEnum.PHONE && !isValidPhoneNumber(value)) {
      throw new InvalidPhoneNumberError();
    }
  };
}
